import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

const TIMEOUT_TO_STOP_CHECKING_FOR_MUTATIONS_AND_INTERACTION = 20000;
const USER_INTERACTION_EVENTS = ['mousedown', 'keydown', 'wheel', 'touchstart'];

type RetryOnMutationProps = {
	callback: () => boolean;
	elementRef?: React.RefObject<Element>;
	skip?: boolean;
};

export const useRetryOnMutation = ({ callback, elementRef, skip }: RetryOnMutationProps) => {
	const stopObserverCallbacks = useRef<(() => void)[]>([]);
	const mutationEndTimer = useRef<number>(0);

	const stopMonitoring = useCallback((_reason: string) => {
		stopObserverCallbacks.current.forEach((stop) => {
			stop();
		});
		stopObserverCallbacks.current = [];

		if (mutationEndTimer.current !== 0) {
			window.clearTimeout(mutationEndTimer.current);
			mutationEndTimer.current = 0;
		}
	}, []);

	const startMonitoring = useCallback(() => {
		if (!stopObserverCallbacks.current.length && elementRef?.current) {
			const mutationObserver = new MutationObserver(() => {
				// Every time there's a change, restart the timer that ends watching for mutations
				// (keep the observer going until changes stop for a while).
				window.clearTimeout(mutationEndTimer.current);
				mutationEndTimer.current = window.setTimeout(() => {
					// De-bouncing ended. Timeout finally running, time to stop observing and trying to follow the anchor
					stopMonitoring('Deep link target never found');
				}, TIMEOUT_TO_STOP_CHECKING_FOR_MUTATIONS_AND_INTERACTION);

				if (callback()) {
					// If the callback succeeded, stop monitoring
					stopMonitoring('Callback succeeded');
				}
			});
			mutationObserver.observe(elementRef.current, {
				childList: true,
				subtree: true,
				attributes: true,
				characterData: true,
			});
			stopObserverCallbacks.current.push(() => {
				mutationObserver.disconnect();
			});

			// Listen for user interaction to end checking for changes
			USER_INTERACTION_EVENTS.forEach((name) => {
				const userInteractionHandler = () => stopMonitoring('User interaction');
				window.addEventListener(name, userInteractionHandler, true);
				stopObserverCallbacks.current.push(() => {
					window.removeEventListener(name, userInteractionHandler, true);
				});
			});
		}
	}, [elementRef, callback, stopMonitoring]);

	useEffect(() => {
		// Do nothing if skip is true or the elementRef is not set.
		if (skip || !elementRef?.current) {
			return;
		}

		// Try right away in case the page has already settled and there are no mutations to observe.
		if (callback()) {
			return;
		}

		// Watch for changes to the page and call the callback whenever one occurs.
		startMonitoring();
		return () => {
			stopMonitoring('Canceled by unmount');
		};
	}, [elementRef, callback, skip, startMonitoring, stopMonitoring]);
};
